/**
 * HarvestRecordForm Component
 *
 * Dialog form for recording new harvests with weight, quality grade,
 * and automatic flush detection. Supports both quick entry and detailed entry modes.
 *
 * User Story: US-P2-007 - Harvest Recording & Yield Tracking
 */

import { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useHarvestMutations } from '@/hooks/useHarvest'
import { toLocalDateString } from '@/utils/dateUtils'
import { Scale, Calendar, User, MessageSquare, Zap } from 'lucide-react'

// Quality grade options with descriptions
const QUALITY_GRADES = [
  { value: 'A+', label: 'A+ (Premium)', description: 'Perfect specimens, premium pricing' },
  { value: 'A', label: 'A (Standard)', description: 'Good quality, standard retail' },
  { value: 'B', label: 'B (Second)', description: 'Minor imperfections, discounted' },
  { value: 'reject', label: 'Reject', description: 'Not saleable, compost or samples' }
]

// Target yield for performance indicator
const TARGET_YIELD_PER_BLOCK = 0.85 // kg

/**
 * Get colour for yield performance indicator
 * @param {number} yieldKg - Yield per block in kg
 * @returns {string} Tailwind colour class
 */
function getYieldColour(yieldKg) {
  if (yieldKg >= 1.0) return 'text-green-600'
  if (yieldKg >= 0.7) return 'text-emerald-600'
  if (yieldKg >= 0.5) return 'text-yellow-600'
  return 'text-red-600'
}

export default function HarvestRecordForm({
  isOpen,
  onClose,
  onSave,
  container = null,     // Container being harvested
  existingHarvests = [] // Previous harvests for this container
}) {
  const { recordHarvest, loading: mutationLoading } = useHarvestMutations()

  const [formData, setFormData] = useState({
    harvest_date: toLocalDateString(new Date()),
    weight_kg: '',
    blocks_harvested: 1,
    quality_grade: 'A',
    harvested_by: '',
    notes: ''
  })

  const [errors, setErrors] = useState({})

  // Calculate estimated flush based on existing harvests
  const estimatedFlush = useMemo(() => {
    if (existingHarvests.length === 0) return 1

    const lastHarvest = existingHarvests[0] // Assuming sorted by date desc
    if (!lastHarvest) return 1

    const lastDate = new Date(lastHarvest.harvest_date)
    const currentDate = new Date(formData.harvest_date)
    const daysDiff = Math.floor((currentDate - lastDate) / (1000 * 60 * 60 * 24))

    // 2+ day gap indicates new flush
    if (daysDiff >= 2) {
      return lastHarvest.flush_number + 1
    }
    return lastHarvest.flush_number
  }, [existingHarvests, formData.harvest_date])

  // Calculate running totals
  const runningTotals = useMemo(() => {
    const totalKg = existingHarvests.reduce((sum, h) => sum + parseFloat(h.weight_kg || 0), 0)
    const harvestCount = existingHarvests.length
    return {
      totalKg: Math.round(totalKg * 100) / 100,
      harvestCount,
      avgPerHarvest: harvestCount > 0 ? Math.round((totalKg / harvestCount) * 100) / 100 : 0
    }
  }, [existingHarvests])

  // Projected total after this harvest
  const projectedTotal = useMemo(() => {
    const newWeight = parseFloat(formData.weight_kg) || 0
    return Math.round((runningTotals.totalKg + newWeight) * 100) / 100
  }, [runningTotals.totalKg, formData.weight_kg])

  // Reset form when opening
  useEffect(() => {
    if (isOpen) {
      setFormData({
        harvest_date: toLocalDateString(new Date()),
        weight_kg: '',
        blocks_harvested: 1,
        quality_grade: 'A',
        harvested_by: '',
        notes: ''
      })
      setErrors({})
    }
  }, [isOpen])

  const validateForm = () => {
    const newErrors = {}

    if (!formData.weight_kg || parseFloat(formData.weight_kg) <= 0) {
      newErrors.weight_kg = 'Weight must be greater than 0'
    }

    if (parseFloat(formData.weight_kg) > 50) {
      newErrors.weight_kg = 'Weight seems unusually high (max 50kg)'
    }

    if (!formData.harvest_date) {
      newErrors.harvest_date = 'Harvest date is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return
    if (!container?.id) {
      setErrors({ submit: 'No container selected' })
      return
    }

    try {
      const harvestData = {
        container_id: container.id,
        harvest_date: formData.harvest_date,
        weight_kg: parseFloat(formData.weight_kg),
        blocks_harvested: parseInt(formData.blocks_harvested) || 1,
        quality_grade: formData.quality_grade,
        harvested_by: formData.harvested_by || undefined,
        notes: formData.notes || undefined
      }

      const result = await recordHarvest(harvestData)

      if (onSave) {
        onSave(result)
      }

      onClose()
    } catch (err) {
      setErrors({ submit: err.message })
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5" />
            Record Harvest
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Container Info Card */}
          {container && (
            <Card className="bg-slate-50">
              <CardContent className="pt-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Container</p>
                    <p className="text-lg font-semibold">{container.code}</p>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className="mb-1">
                      Flush {estimatedFlush}
                    </Badge>
                    <p className="text-xs text-slate-500">Auto-detected</p>
                  </div>
                </div>

                {runningTotals.harvestCount > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <p className="text-xs text-slate-500 mb-1">Previous harvests</p>
                    <div className="flex gap-4 text-sm">
                      <span>{runningTotals.totalKg} kg total</span>
                      <span>{runningTotals.harvestCount} harvests</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Harvest Date */}
          <div className="space-y-2">
            <Label htmlFor="harvest_date" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Harvest Date
            </Label>
            <Input
              id="harvest_date"
              type="date"
              value={formData.harvest_date}
              onChange={(e) => handleInputChange('harvest_date', e.target.value)}
              className={errors.harvest_date ? 'border-red-500' : ''}
            />
            {errors.harvest_date && (
              <p className="text-sm text-red-500">{errors.harvest_date}</p>
            )}
          </div>

          {/* Weight */}
          <div className="space-y-2">
            <Label htmlFor="weight_kg" className="flex items-center gap-2">
              <Scale className="h-4 w-4" />
              Weight (kg)
            </Label>
            <Input
              id="weight_kg"
              type="number"
              step="0.01"
              min="0"
              max="50"
              placeholder="e.g. 0.85"
              value={formData.weight_kg}
              onChange={(e) => handleInputChange('weight_kg', e.target.value)}
              className={errors.weight_kg ? 'border-red-500' : ''}
            />
            {errors.weight_kg && (
              <p className="text-sm text-red-500">{errors.weight_kg}</p>
            )}

            {/* Projected total indicator */}
            {formData.weight_kg && projectedTotal > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <Zap className="h-4 w-4 text-amber-500" />
                <span>
                  Projected total: {' '}
                  <span className={getYieldColour(projectedTotal)}>
                    {projectedTotal} kg
                  </span>
                  {projectedTotal >= TARGET_YIELD_PER_BLOCK && (
                    <span className="text-green-600 ml-1">(on target)</span>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Quality Grade */}
          <div className="space-y-2">
            <Label htmlFor="quality_grade">Quality Grade</Label>
            <Select
              value={formData.quality_grade}
              onValueChange={(value) => handleInputChange('quality_grade', value)}
            >
              <SelectTrigger id="quality_grade">
                <SelectValue placeholder="Select grade" />
              </SelectTrigger>
              <SelectContent>
                {QUALITY_GRADES.map(grade => (
                  <SelectItem key={grade.value} value={grade.value}>
                    <div className="flex items-center gap-2">
                      <span>{grade.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              {QUALITY_GRADES.find(g => g.value === formData.quality_grade)?.description}
            </p>
          </div>

          {/* Harvested By */}
          <div className="space-y-2">
            <Label htmlFor="harvested_by" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Harvested By (optional)
            </Label>
            <Input
              id="harvested_by"
              placeholder="Name of harvester"
              value={formData.harvested_by}
              onChange={(e) => handleInputChange('harvested_by', e.target.value)}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Notes (optional)
            </Label>
            <Textarea
              id="notes"
              placeholder="Any observations about this harvest..."
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={2}
            />
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <Alert variant="destructive">
              <AlertDescription>{errors.submit}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutationLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={mutationLoading}>
            {mutationLoading ? 'Recording...' : 'Record Harvest'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
